import { useState, useEffect, useContext } from 'react';
import  ActionType from '../../../store/ActionType';
import { GlobalContext } from '../../../store/Store';
import Api from '../../../apis/meshApi';
import { useInterval } from './PollingInterval';
import useLocalStorage from './PollingStorage';
import { getCombinedProgress } from '../../mike-topbar-progress-spinner';
import { getListMeshStatus, getMeshBoundaries, getTileGridConfig } from '../apiFunctions';
import { ERRORLIST } from '../../../Models/IErrorList';

function PollingWrapper() {
  const [pollingSettings, setPollingSettings] = useLocalStorage('polling-settings', []);
  const [pollingDelay, setPollingDelay] = useState(null);
  const [globalState, globalDispatch] = useContext(GlobalContext);
  const defaultPollingDelay = 3000;
  const jobStorageDeletionInHours = 120;
 
  useEffect(() => {
    globalDispatch({
      type: ActionType.SET_POLLING,
      data: pollingSettings,
    });
  }, []);

  useEffect(() => {
    if (globalState?.pollingIds?.length > 0) setPollingDelay(defaultPollingDelay);
    if (globalState?.pollingIds?.length === 0) setPollingDelay(null);
  }, [globalState.pollingIds]);

  useInterval(async () => {
    const statusPolling = await Api.getExtractionBuilderJobs(globalState?.pollingIds);
    const completedPollIds = [];
    const failedPollIds = [];
    const jobStatus = statusPolling.data as any;
    if (!Array.isArray(jobStatus)) return;
    jobStatus.filter(item => {
      if (globalState?.pollingIds?.indexOf(item.JobId) !== -1 && item.State === 'FAILED') {
        globalDispatch({
          type: ActionType.SET_MESSAGE,
          message: `Failed to call the extraction service: ${item.Status}`,
        });
        globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
        globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
        globalDispatch({
          type: ActionType.ADDUP_POLLING_ITEM,
          data: { id: item.JobId, error: true },
        });
        globalDispatch({
          type: ActionType.ADD_NOTIFICATION,
          data: {
            projectId: globalState.project.currentProject.projectGuid,
            notification: {
              title: 'Operation failed',
              message: `${item.Status}`,
              time: Date.now(),
              error: true,
              messages: item.OperationId === 'ExtractInitialConditions' && item.Status.includes(ERRORLIST) && JSON.parse(item.Status)
            },
          },
        });

        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: {
            id: item.JobId,
          },
        });
        globalDispatch({ type: ActionType.SET_MESSAGE, message: item.Status });
        globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
        globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
        failedPollIds.push(item.JobId);
      }
      if (globalState?.pollingIds?.indexOf(item.JobId) !== -1 && ['PARTIALLYSUCCEEDED', 'COMPLETED'].includes(item.State)) {
        if (item.OperationId === 'BoundaryExtraction') {
          getListMeshStatus({ globalContext: { state: globalState, dispatch: globalDispatch } });
          getMeshBoundaries({
            meshName: item.MeshName,
            globalContext: { state: globalState, dispatch: globalDispatch },
          });
        }
        if (item.OperationId === 'MeshTiling') {
          getTileGridConfig({
            meshName: item.MeshName,
            projectID: globalState.project.currentProject.projectGuid,
            globalContext: { state: globalState, dispatch: globalDispatch },
          });
        }
        if (item.OperationId === 'ExtractInitialConditions') {
          if (item.State === 'PARTIALLYSUCCEEDED'){            
            globalDispatch({
              type: ActionType.ADD_NOTIFICATION,
              data: {
                projectId: globalState.project.currentProject.projectGuid,
                notification: {
                  time: Date.now(),
                  title: 'Extraction partially succeeded',
                  message: `Extraction is partially complete for ${item.MeshName}`,
                  link: true,
                  linkLabel: 'Open file location',
                  messages: item.Status.includes(ERRORLIST) && JSON.parse(item.Status)
                },
              },
            });
          }
          else{
            globalDispatch({
              type: ActionType.ADD_NOTIFICATION,
              data: {
                projectId: globalState.project.currentProject.projectGuid,
                notification: {
                  time: Date.now(),
                  title: 'Extraction succeeded',
                  message: `Extraction is complete for ${item.MeshName}`,
                  link: true,
                  linkLabel: 'Open file location',
                },
              },
            });
          }          
        }
       
        if (item.State === 'PARTIALLYSUCCEEDED'){
          globalDispatch({ type: ActionType.SET_MESSAGE, message: "Data partially ready for download" });
          globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'warning' });
          globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
        }
        else{
          globalDispatch({ type: ActionType.SET_MESSAGE, message: item.Status });
          globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'success' });
          globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
        }
 
        completedPollIds.push(item.JobId);
      }

      if (globalState?.pollingIds?.indexOf(item.JobId) !== -1) {
        const jobTimestamp = new Date(item.Timestamp);
        const now = new Date();
        const msBetweenDates = Math.abs(jobTimestamp.getTime() - now.getTime());
        const hoursBetweenDates = msBetweenDates / (60 * 60 * 1000);
        if (hoursBetweenDates > jobStorageDeletionInHours) {
          globalDispatch({
            type: ActionType.ADD_NOTIFICATION,
            data: {
              projectId: globalState.project.currentProject.projectGuid,
              notification: {
                title: 'Operation failed',
                message: `An operation ${item.JobId} has failed to complete after running more than ${jobStorageDeletionInHours} hour's `,
                time: Date.now(),
                error: true,
              },
            },
          });
          failedPollIds.push(item.JobId);
        } else {
          if (
            (globalState?.pollingIds?.indexOf(item.JobId) !== -1 && item.State === 'STARTED') ||
            (globalState?.pollingIds?.indexOf(item.JobId) !== -1 && item.State === 'PROCESSING')
          ) {
            const match = globalState.pollingItems.find(plitem => plitem.id === item.JobId);
            const calcProgress =
              item.OperationsTotal !== 0
                ? (item.OperationsCounter / item.OperationsTotal) * 100
                : 0;

            if (!match) {
              globalDispatch({
                type: ActionType.ADDUP_POLLING_ITEM,
                data: {
                  title:
                    'Step ' +
                    item.OperationsCounter +
                    ' of ' +
                    item.OperationsTotal +
                    ': ' +
                    item.Status,
                  id: item.JobId,
                  progressValue: calcProgress,
                  browserStep: 0,
                  canBeDeleted: true,
                },
              });
            } else {
              const combProgress = getCombinedProgress(calcProgress, match.browserStep);
              globalDispatch({
                type: ActionType.ADDUP_POLLING_ITEM,
                data: {
                  title:
                    'Step ' +
                    item.OperationsCounter +
                    ' of ' +
                    item.OperationsTotal +
                    ': ' +
                    item.Status,
                  id: item.JobId,
                  progressValue: combProgress,
                  browserStep: match.browserStep,
                  canBeDeleted: true,
                },
              });
            }
          }
          if (globalState?.pollingIds?.indexOf(item.JobId) !== -1 && item.State === 'SCHEDULED') {
            globalDispatch({
              type: ActionType.ADDUP_POLLING_ITEM,
              data: {
                title: item.Status,
                id: item.JobId,
                progressValue: 0,
                browserStep: 0,
              },
            });
          }
        }
      }

      return item;
    });

    if (failedPollIds.length > 0) {
      globalDispatch({
        type: ActionType.SET_POLLING_REMOVELS,
        data: failedPollIds,
      });
      setPollingSettings(
        [...globalState?.pollingIds].filter(poll => failedPollIds.indexOf(poll) === -1)
      );
    }

    if (completedPollIds.length > 0) {
      completedPollIds.map(id => {
        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: {
            id: id,
          },
        });
      });
      globalDispatch({
        type: ActionType.SET_POLLING_REMOVELS,
        data: completedPollIds,
      });
      setPollingSettings(
        [...globalState?.pollingIds].filter(poll => completedPollIds.indexOf(poll) === -1)
      );
    }

    //console.log('POLLING ', globalState?.pollingIds, statusPolling, completedPollIds);
    // FOR DEBUG: STOP THE POLLING setPollingDelay(null);
  }, pollingDelay);
}

export default PollingWrapper;
